import "./index.scss";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

import { Language } from "../../../config";
import { store } from "../../../Store";
import { tutorialFor } from "../../../data/tutorials";
import { hasSeenTutorial, markTutorialSeen } from "../../../utils/tutorialSeen";
import useReducedMotion from "../../../Hooks/useReducedMotion";
import { handAt } from "./gesture";

/**
 * A game's first-play tutorial (2026-11-03): a hand shows the first move on
 * the real game, with a few words, and the child does it. Nothing is blocked:
 * the overlay lets every tap through, and a step ends when the child does the
 * thing (see data/tutorials.js). Plays once per device; Skip ends it too.
 *
 * It waits for its target to appear (after the round banner, say), and skips a
 * step whose target never does, so it can't get stuck on screen.
 */

const GIVE_UP_MS = 9000; // a target that never shows up
// all of it on screen (a bubble still rising from below isn't ready yet)
const visible = (el) => {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 4 || r.height < 4 || r.top < 0 || r.bottom > window.innerHeight) return null;
  return r;
};
// not while a round banner is up
const bannerUp = () => Boolean(document.querySelector(".roundbanner_banner"));
const middle = (r) => ({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
const tries = () => {
  const { hits = 0, misses = 0 } = store.getState().store.value.stats ?? {};
  return hits + misses;
};

/** The pointing hand, drawn here (no image files). Its fingertip is at 0, 0. */
const Hand = () => (
  <svg className="tutorial_handArt" viewBox="-14 -6 86 104" aria-hidden="true" focusable="false">
    <path
      d="M10 4 C10 -3 22 -3 22 4 L22 38 C24 32 35 32 35 40 C37 34 48 34 48 42 C51 37 62 38 62 47 L62 70 C62 86 52 96 36 96 L28 96 C16 96 8 88 2 78 L-8 58 C-12 50 -2 44 4 50 L10 58 Z"
      fill="#ffffff"
      stroke="#3b2412"
      strokeWidth="5"
      strokeLinejoin="round"
    />
    <path d="M35 40 L35 54 M48 42 L48 56" stroke="#3b2412" strokeWidth="4" strokeLinecap="round" />
  </svg>
);

const Tutorial = ({ gameType, force = false }) => {
  const steps = tutorialFor(gameType);
  const reduced = useReducedMotion();
  const [on, setOn] = useState(() => Boolean(steps) && (force || !hasSeenTutorial(gameType, Language)));
  const [step, setStep] = useState(0);
  const [shown, setShown] = useState(false); // its target is on screen
  const handRef = useRef(null);
  const ringRef = useRef(null);
  const captionRef = useRef(null);
  const reducedRef = useRef(reduced);
  reducedRef.current = reduced;

  const finish = () => {
    markTutorialSeen(gameType, Language);
    setOn(false);
  };

  // follow the step's target every frame (things move: bubbles, moles...)
  useEffect(() => {
    if (!on || !steps) return undefined;
    const current = steps[step];
    if (!current) {
      finish();
      return undefined;
    }
    let frame = 0;
    let start = null;
    let waitedFrom = performance.now();
    let isShown = false;
    const startTries = tries();
    let tapped = false;
    let first = null; // for "gone": the element the step began on
    let travelled = 0; // for "moved": how far the pointer has gone, or an arrow key
    let lastPoint = null;
    const onMove = (e) => {
      if (lastPoint) travelled += Math.hypot(e.clientX - lastPoint.x, e.clientY - lastPoint.y);
      lastPoint = { x: e.clientX, y: e.clientY };
    };
    const onKey = (e) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") travelled = Infinity;
    };
    document.addEventListener("pointermove", onMove, true);
    document.addEventListener("keydown", onKey, true);

    const onTap = (e) => {
      const el = document.querySelector(current.target);
      if (el && el.contains(e.target)) tapped = true;
    };
    document.addEventListener("pointerup", onTap, true);
    document.addEventListener("click", onTap, true);

    const advance = () => setStep((s) => s + 1);

    const tick = (now) => {
      const r = bannerUp() ? null : visible(document.querySelector(current.target));
      const toR = current.to ? visible(document.querySelector(current.to)) : null;
      const ready = r && (!current.to || toR);
      if (ready !== isShown) {
        isShown = Boolean(ready);
        setShown(isShown);
        if (isShown && start === null) start = now;
      }
      if (!ready && now - waitedFrom > GIVE_UP_MS) {
        advance();
        return;
      }
      if (ready) {
        waitedFrom = now;
        const from = middle(r);
        const to = toR ? middle(toR) : null;
        const hand = reducedRef.current
          ? { ...from, press: 0, fade: 1 }
          : handAt({ gesture: current.gesture, from, to, width: r.width, ms: now - start });
        if (handRef.current) {
          handRef.current.style.transform = `translate(${hand.x}px, ${hand.y}px) scale(${1 - hand.press * 0.12})`;
          handRef.current.style.opacity = String(hand.fade);
        }
        if (ringRef.current) {
          const pad = 10;
          Object.assign(ringRef.current.style, {
            left: `${r.left - pad}px`,
            top: `${r.top - pad}px`,
            width: `${r.width + pad * 2}px`,
            height: `${r.height + pad * 2}px`,
          });
        }
        // the caption keeps clear of both ends of a drag, so it never hides
        // where the child is meant to put something
        const cap = captionRef.current;
        if (cap) {
          const w = cap.offsetWidth;
          const h = cap.offsetHeight;
          const top = Math.min(r.top, toR?.top ?? r.top);
          const bottom = Math.max(r.bottom, toR?.bottom ?? r.bottom);
          const x = Math.min(window.innerWidth - w - 8, Math.max(8, from.x - w / 2));
          // nor the word the child is looking for
          const word = document.querySelector(".wordprompt_sign, .fmty_card")?.getBoundingClientRect();
          const covers = (y) =>
            word && x < word.right && x + w > word.left && y < word.bottom && y + h > word.top;
          const fits = (y) => y >= 8 && y + h <= window.innerHeight - 8;
          const y =
            [top - h - 26, bottom + 70, window.innerHeight - h - 8, 8].find((c) => fits(c) && !covers(c)) ??
            (fits(top - h - 26) ? top - h - 26 : 8);
          cap.style.transform = `translate(${x}px, ${y}px)`;
        }
      }
      const moved = tries() !== startTries;
      if (r && !first) first = document.querySelector(current.target);
      const gone = current.done === "gone" && first && (!first.isConnected || !first.matches(current.target));
      const didIt =
        (current.done === "tap" && tapped) || (current.done === "moved" && isShown && travelled > 160) || gone;
      if (moved || didIt) {
        // a tap step may lead straight into the next one; a scored try ends it all
        if (moved && current.done === "move") {
          finish();
          return;
        }
        advance();
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("pointerup", onTap, true);
      document.removeEventListener("click", onTap, true);
      document.removeEventListener("pointermove", onMove, true);
      document.removeEventListener("keydown", onKey, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, step]);

  if (!on || !steps || !steps[step]) return null;
  // drawn on the page itself: inside a game's layout, a transformed parent (the
  // story corner's sliding page) would shift everything that's fixed to the screen
  return createPortal(
    <div className={`tutorial${shown ? " tutorial--shown" : ""}`} aria-live="polite">
      <div ref={ringRef} className="tutorial_ring" aria-hidden="true" />
      <div ref={handRef} className="tutorial_hand" aria-hidden="true">
        <Hand />
      </div>
      <div ref={captionRef} className="tutorial_caption">
        <span key={step} className="tutorial_text badaBoom">
          {steps[step].text}
        </span>
        <button type="button" className="tutorial_skip" onClick={finish}>
          Skip
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default Tutorial;

Tutorial.propTypes = {
  gameType: PropTypes.string,
  force: PropTypes.bool,
};
